import {
  IMAGE_MODELS,
  getImageCreativeProfile,
  getImageModel,
} from '../../config/imageModels';

export const DEFAULT_MORE_LIKE_THIS_MODEL_KEY = 'nano-banana-2-flash-edit';
export const MORE_LIKE_THIS_ROUTING_STRATEGY = 'app-default-edit-model';

function isUsableMoreLikeThisModel(model) {
  return Boolean(
    model
      && typeof model.service?.edit === 'function'
      && (model.capabilities?.maxInputImages?.edit || 0) >= 1
  );
}

function assertUsableEditModel(modelKey, model) {
  if (!model) {
    throw new Error(`Unknown More Like This model: ${modelKey}`);
  }
  if (!isUsableMoreLikeThisModel(model)) {
    throw new Error(`More Like This model is not a usable image edit model: ${modelKey}`);
  }
}

export function normalizeMoreLikeThisModelKey(modelKey) {
  const model = getImageModel(modelKey);
  if (!isUsableMoreLikeThisModel(model)) {
    return DEFAULT_MORE_LIKE_THIS_MODEL_KEY;
  }
  return model.editKey || model.key;
}

export function getMoreLikeThisModelOptions() {
  return IMAGE_MODELS
    .filter(isUsableMoreLikeThisModel)
    .map((model) => ({
      value: model.editKey || model.key,
      label: model.label,
      modelName: model.cost || model.qualityTier || null,
      iconKey: model.iconKey,
    }));
}

function resolveSupportedResolution(model, item) {
  const supported = model.capabilities?.resolutions || [];
  if (supported.length === 0) return null;
  const sourceResolution = item?.generationParams?.resolution;
  if (sourceResolution && supported.includes(sourceResolution)) {
    return sourceResolution;
  }
  return model.capabilities?.defaultResolution || supported[0] || null;
}

function resolveAspectRatio(model, item) {
  const supported = model.capabilities?.aspectRatios || [];
  const sourceAspectRatio = item?.generationParams?.aspectRatio;
  if (sourceAspectRatio && (supported.length === 0 || supported.includes(sourceAspectRatio))) {
    return sourceAspectRatio;
  }
  if (model.key === 'nano-banana-2' || model.key === 'nano-banana-2-flash') {
    return 'auto';
  }
  return supported[0] || '1:1';
}

export function getMoreLikeThisSourceModelKey(item) {
  return item?.metadata?.runRecord?.execution?.modelKey
    || item?.generationParams?.module
    || item?.generationParams?.model
    || null;
}

/**
 * A Midjourney image carries its parent grid job id + grid index in
 * generationParams (persisted). If present, "more like this" should run a
 * NATIVE Midjourney variation of that grid image (parentJobId + 1-based index)
 * rather than the generic edit-model path. Returns null for non-MJ images.
 */
export function resolveMidjourneyVariationSource(item) {
  const gp = item?.generationParams || {};
  const parentJobId = gp.midjourneyParentJobId;
  const gridIndex = gp.midjourneyGridIndex;
  if (!parentJobId || !Number.isInteger(gridIndex)) return null;
  return { parentJobId, index: gridIndex + 1 };
}

function resolveMidjourneyVariationRoute(item) {
  const source = resolveMidjourneyVariationSource(item);
  if (!source) return null;
  // NOTE: the transformed IMAGE_MODELS entries do NOT carry `.provider` — use
  // the stable `qualityTier` marker.
  const model = IMAGE_MODELS.find((candidate) => candidate.qualityTier === 'midjourney');
  if (!model) return null;
  const capabilityProfile = getImageCreativeProfile(model.key, 'generate');
  if (!capabilityProfile) return null;

  return {
    modelKey: model.key,
    baseModelKey: model.key,
    modelName: model.label,
    iconKey: model.iconKey || 'midjourney',
    qualityTier: model.qualityTier || null,
    defaultResolution: null,
    defaultAspectRatio: resolveAspectRatio(model, item),
    capabilityProfile,
    // Ignores the generic edit params (imageUrls/prompt) — a native variation
    // acts on the parent grid job.
    loadGenerate: async () => {
      const { loadProviderExport } = await import('../providers/providerServiceLoaders.js');
      const variationFn = await loadProviderExport('midjourney', 'generateMidjourneyVariation');
      return (callParams = {}) => variationFn({
        parentJobId: source.parentJobId,
        index: source.index,
        onStatus: callParams.onStatus,
        onImageComplete: callParams.onImageComplete,
        signal: callParams.signal,
      });
    },
    routingStrategy: 'midjourney-native-variation',
    resultCount: 4,
    sourceModelKey: getMoreLikeThisSourceModelKey(item),
  };
}

export function resolveMoreLikeThisRoute({
  modelKey = DEFAULT_MORE_LIKE_THIS_MODEL_KEY,
  item = null,
} = {}) {
  // Midjourney images get a native grid variation instead of the generic
  // edit-model path; falls through when the item is not a MJ grid image.
  const midjourneyRoute = resolveMidjourneyVariationRoute(item);
  if (midjourneyRoute) return midjourneyRoute;

  const resolvedModelKey = normalizeMoreLikeThisModelKey(modelKey);
  const model = getImageModel(resolvedModelKey);
  assertUsableEditModel(resolvedModelKey, model);

  const editModelKey = model.editKey || resolvedModelKey;
  const capabilityProfile = getImageCreativeProfile(editModelKey, 'edit');
  if (!capabilityProfile) {
    throw new Error(`More Like This model has no creative profile: ${editModelKey}`);
  }

  return {
    modelKey: editModelKey,
    baseModelKey: model.key,
    modelName: model.label,
    iconKey: model.iconKey || 'generic-ai',
    qualityTier: model.qualityTier || null,
    defaultResolution: resolveSupportedResolution(model, item),
    defaultAspectRatio: resolveAspectRatio(model, item),
    capabilityProfile,
    loadGenerate: model.service.edit,
    routingStrategy: MORE_LIKE_THIS_ROUTING_STRATEGY,
    sourceModelKey: getMoreLikeThisSourceModelKey(item),
  };
}
